import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { Observable } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core';
import { QUERY_PRODUCT, QUERY_PRODUCTS, QUERY_RECOMMENDED_PRODUCTS, MUTATION_WRITE_REVIEW, QUERY_HOME_PRODUCTS, QUERY_HOME_CATEGORIES, QUERY_HOME_BRANDS } from '../queries/product';

type ProductByIdVars = { id: string | number };
type ProductsVars = { 
  items: number; 
  after: string | null; 
  tags?: string[] | null;
  search?: string | null;
  maxPrice?: number | null;
  categoryIn?: string[] | null;
  rating?: number[] | null;
  onSale?: boolean | null;
  featured?: boolean | null;
  stockStatus?: string[] | null;
  orderby?: any[] | null;
  brands?: string[] | null;
};
type RecommendedProductsVars = { excludeId: number };

@Injectable({providedIn: 'root'})
export class ProductGraphqlService {
  constructor(private readonly apollo: Apollo) {
  }

  // Watch a single product by ID
  watchProduct(id: string | number): QueryRef<any, ProductByIdVars> {
    return this.apollo.watchQuery<any, ProductByIdVars>({
      fetchPolicy: 'no-cache',
      query: QUERY_PRODUCT,
      variables: {id}
    });
  }

  // Watch list of products (catalog) with pagination and filters
  watchProducts(
    limit: number, 
    after?: string | null, 
    search?: string, 
    maxPrice?: number, 
    tags?: string[],
    categoryIn?: string[],
    rating?: number[],
    onSale?: boolean,
    featured?: boolean,
    stockStatus?: string[],
    orderby?: any[],
    brands?: string[]
  ): QueryRef<any, ProductsVars> {
    return this.apollo.watchQuery<any, ProductsVars>({
      fetchPolicy: 'no-cache',
      query: QUERY_PRODUCTS,
      variables: {
        items: limit,
        after: after || '',
        search: search || null,
        maxPrice: maxPrice || null,
        tags: tags?.length ? tags : null,
        categoryIn: categoryIn?.length ? categoryIn : null,
        rating: rating?.length ? rating : null,
        onSale: onSale ?? null,
        featured: featured ?? null,
        stockStatus: stockStatus?.length ? stockStatus : null,
        orderby: orderby?.length ? orderby : null,
        brands: brands?.length ? brands : null
      }
    });
  }

  // Simple query for recommended products, excluding a given product ID
  queryRecommendedProducts(excludeId: number): Observable<ApolloQueryResult<any>> {
    return this.apollo.query<any, RecommendedProductsVars>({
      fetchPolicy: 'no-cache',
      query: QUERY_RECOMMENDED_PRODUCTS,
      variables: {excludeId}
    });
  }

  // Write a new review
  writeReview(input: any): Observable<any> {
    return this.apollo.mutate({
      mutation: MUTATION_WRITE_REVIEW,
      variables: { input }
    });
  }

  queryHomeProducts(tags?: string[]): Observable<ApolloQueryResult<any>> {
    return this.apollo.query<any>({
      fetchPolicy: 'cache-first',
      query: QUERY_HOME_PRODUCTS,
      variables: { tags: tags?.length ? tags : null }
    });
  }

  queryHomeCategories(): Observable<ApolloQueryResult<any>> {
    return this.apollo.query<any>({
      fetchPolicy: 'cache-first',
      query: QUERY_HOME_CATEGORIES
    });
  }

  queryHomeBrands(tags?: string[]): Observable<ApolloQueryResult<any>> {
    return this.apollo.query<any>({
      fetchPolicy: 'cache-first',
      query: QUERY_HOME_BRANDS,
      variables: { tags: tags?.length ? tags : null }
    });
  }
}
